// frontend/src/features/ups/ManageSites.js
// UPS Admin: Manage Sites (control-plane)
// - Loads ALL sites (active + inactive) using loadUpsAdminSites
// - Search + status filter + open drawer + create modal

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  loadUpsAdminSites,
  selectUpsAdminSites,
  selectSitesLoading,
  selectSitesError,
} from '../sites/sitesSlice';

import CreateSiteModal from './modals/CreateSiteModal';
import SiteDrawer from './modals/SiteDrawer';
import DriverMobileCard from './components/DriverMobileCard';
import { COMPANY_ACTION_CLASSES, CompanyStatusBadge } from './ui';

function normalizeStatus(s) {
  const v = (s ?? '').toString().trim().toLowerCase();
  if (v === 'inactive') return 'inactive';
  return 'active';
}

export default function ManageSites() {
  const dispatch = useDispatch();
  const searchInputId = 'manage-sites-search';

  const sites = useSelector(selectUpsAdminSites);
  const loading = useSelector(selectSitesLoading);
  const error = useSelector(selectSitesError);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);

  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    dispatch(loadUpsAdminSites());
  }, [dispatch]);

  const filtered = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase();
    const st = (statusFilter ?? 'all').toLowerCase();

    return (sites || [])
      .filter((s) => {
        const sStatus = normalizeStatus(s.status);
        if (st === 'all') return true;
        return sStatus === st;
      })
      .filter((s) => {
        if (!q) return true;
        const name = (s.name || '').toLowerCase();
        const code = (s.code || '').toLowerCase();
        return name.includes(q) || code.includes(q);
      });
  }, [sites, query, statusFilter]);

  const openDrawer = (site) => {
    setCurrentSite(site);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setCurrentSite(null);
  };

  const refresh = () => dispatch(loadUpsAdminSites());

  const onCreated = (site) => {
    // Refresh list and open drawer for the newly created site.
    refresh();
    setShowCreate(false);
    if (site) openDrawer(site);
  };

  const totalCount = sites?.length || 0;
  const activeCount = (sites || []).filter((s) => normalizeStatus(s.status) === 'active').length;
  const inactiveCount = totalCount - activeCount;

  const filterButtonClass = (value) =>
    `rounded-xl border px-4 py-3 text-sm font-semibold transition ${
      statusFilter === value
        ? 'border-app-primary bg-app-primary text-white shadow-sm'
        : 'border-app-border bg-app-card text-app-muted hover:border-app-primary/40 hover:bg-app-surface hover:text-app-text'
    }`;

  return (
    <>
      <div className="space-y-6 p-6">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-app-accent">
            Administration
          </p>
          <h1 className="mt-2 text-2xl font-bold text-app-text">Manage Sites</h1>
          <p className="mt-1 text-sm text-app-muted">
            Control-plane list including active and inactive sites.
          </p>
        </div>

        <button
          onClick={() => setShowCreate(true)}
          className={COMPANY_ACTION_CLASSES.primary}
        >
          New site
        </button>
      </div>

      {/* Search, filters, and refresh controls */}
      <section className="rounded-2xl border border-app-border bg-app-card p-5 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
            <div>
              <label
                htmlFor={searchInputId}
                className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
              >
                Search
              </label>
              <input
                id={searchInputId}
                name="siteSearch"
                className="mt-2 w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
                placeholder="Search site name or code…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={() => setStatusFilter('all')} className={filterButtonClass('all')}>
                All ({totalCount})
              </button>
              <button
                onClick={() => setStatusFilter('active')}
                className={filterButtonClass('active')}
              >
                Active ({activeCount})
              </button>
              <button
                onClick={() => setStatusFilter('inactive')}
                className={filterButtonClass('inactive')}
              >
                Inactive ({inactiveCount})
              </button>
            </div>
          </div>

          <button
            onClick={refresh}
            disabled={loading}
            className={COMPANY_ACTION_CLASSES.secondary}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}
      </section>

      {/* Sites table */}
      <section className="overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-app-text">Sites</h2>
            <p className="mt-1 text-sm text-app-muted">
              Open a site to rename it, review agencies, or manage availability.
            </p>
          </div>

          <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
            {filtered.length} shown
          </div>
        </div>

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-full bg-app-card text-sm">
            <thead className="border-b border-app-border bg-app-surface">
              <tr>
                {['ID', 'Site', 'Code', 'Status', 'Actions'].map((col) => (
                  <th
                    key={col}
                    className={`px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-app-muted ${
                      col === 'Actions' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-app-border">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-app-muted">
                    Loading sites…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-app-muted">
                    <div>
                      {query.trim() ? (
                        <>No sites match “{query.trim()}”.</>
                      ) : statusFilter === 'inactive' ? (
                        <>No inactive sites.</>
                      ) : statusFilter === 'active' ? (
                        <>No active sites.</>
                      ) : (
                        <>No sites yet. Create your first site.</>
                      )}
                    </div>

                    <button
                      onClick={() => setShowCreate(true)}
                      className={`mt-4 ${COMPANY_ACTION_CLASSES.primary}`}
                    >
                      New site
                    </button>
                  </td>
                </tr>
              ) : (
                filtered.map((s) => {
                  const status = normalizeStatus(s.status);
                  const statusTone = status === 'active' ? 'active' : 'neutral';

                  return (
                    <tr key={s.id} className="transition hover:bg-app-surface">
                      <td className="whitespace-nowrap px-6 py-5 text-sm text-app-muted">{s.id}</td>

                      <td className="px-6 py-5">
                        <button onClick={() => openDrawer(s)} className="text-left">
                          <div className="font-semibold text-app-text">{s.name || '—'}</div>
                          <div className="mt-1 text-xs text-app-muted">
                            {s.created_at
                              ? `Created · ${new Date(s.created_at).toLocaleString()}`
                              : 'Created · —'}
                          </div>
                        </button>
                      </td>

                      <td className="whitespace-nowrap px-6 py-5 font-mono text-xs text-app-text">
                        {s.code || '—'}
                      </td>

                      <td className="whitespace-nowrap px-6 py-5">
                        <CompanyStatusBadge tone={statusTone} className="capitalize">
                          {status}
                        </CompanyStatusBadge>
                      </td>

                      <td className="whitespace-nowrap px-6 py-5 text-right">
                        <button
                          onClick={() => openDrawer(s)}
                          className={COMPANY_ACTION_CLASSES.compactSecondary}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 xl:hidden">
          {loading ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              Loading sites...
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              <div>
                {query.trim() ? (
                  <>No sites match "{query.trim()}".</>
                ) : statusFilter === 'inactive' ? (
                  <>No inactive sites.</>
                ) : statusFilter === 'active' ? (
                  <>No active sites.</>
                ) : (
                  <>No sites yet. Create your first site.</>
                )}
              </div>

              <button
                onClick={() => setShowCreate(true)}
                className={`mt-4 ${COMPANY_ACTION_CLASSES.primary}`}
              >
                New site
              </button>
            </div>
          ) : (
            filtered.map((s) => {
              const status = normalizeStatus(s.status);
              const statusTone = status === 'active' ? 'active' : 'neutral';
              const avatarText = (s.name || s.code || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2);

              return (
                <DriverMobileCard
                  key={s.id}
                  tone="internal"
                  title={s.name || '-'}
                  subtitle={`Site ID - ${s.id || '-'}`}
                  avatarText={avatarText}
                  badges={[
                    <CompanyStatusBadge key="status" tone={statusTone} className="capitalize">
                      {status}
                    </CompanyStatusBadge>,
                  ]}
                  fields={[
                    ['Code', s.code],
                    [
                      'Created',
                      s.created_at ? new Date(s.created_at).toLocaleString() : null,
                    ],
                  ]}
                  actions={
                    <button
                      onClick={() => openDrawer(s)}
                      className={COMPANY_ACTION_CLASSES.compactSecondary}
                    >
                      Manage
                    </button>
                  }
                />
              );
            })
          )}
        </div>
      </section>

    </div>

    {showCreate && (
      <CreateSiteModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={onCreated}
      />
    )}

    {/* Drawer is outside space-y-6 so it does not receive sibling margin */}
    <SiteDrawer
      isOpen={drawerOpen}
      site={currentSite}
      onClose={closeDrawer}
      onChanged={refresh}
    />
  </>
);
}
